import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../Layout/Header';
import Footer from '../../Layout/Footer';
import SeoTagsFooter from './SeoTagsFooter';
import Seo, { SITE_URL } from '../../Layout/Seo';

// NOTE: This reads its content from the query string (?title=, ?excerpt=,
// ?category=, ?date=, ?body=), the same pattern ServiceDetail.jsx already
// uses. That means it will render correctly for any post once Blog.jsx
// (currently also an empty stub) links to /blog_detail with those params.
//
// Same caveat as ServiceDetail.jsx: every post shares one physical URL
// (/blog_detail?title=...), which is weaker for indexing than a real
// path per post. Once you have real posts, the higher-leverage move is
// switching to /blog/:slug and adding each slug to sitemap.xml.

function BlogDetails() {
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const titleParam = searchParams.get('title') || 'Fintech & API Insights';
  const categoryParam = searchParams.get('category') || 'Fintech';
  const dateParam = searchParams.get('date') || '';
  const excerptParam =
    searchParams.get('excerpt') ||
    `Insights on ${titleParam.toLowerCase()} from the Mokshiq team, covering AEPS, DMT, recharge APIs, and digital payment technology in India.`;
  const bodyParam =
    searchParams.get('body') ||
    'This article is coming soon. Check back shortly, or contact our team at sales@mokshiq.in for more information on this topic.';

  const metaDescription =
    excerptParam.length > 155 ? `${excerptParam.slice(0, 152)}...` : excerptParam;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: titleParam,
    description: metaDescription,
    articleSection: categoryParam,
    ...(dateParam ? { datePublished: dateParam } : {}),
    author: {
      '@type': 'Organization',
      name: 'Mokshiq',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Mokshiq',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/assets/images/logo.png`,
      },
    },
    mainEntityOfPage: `${SITE_URL}/blog_detail${location.search}`,
  };

  return (
    <div className="page_wrapper">
      <Seo
        title={`${titleParam} | Mokshiq Blog`}
        description={metaDescription}
        keywords={`${titleParam}, ${categoryParam}, Mokshiq blog, fintech India`}
        path={`/blog_detail${location.search}`}
        jsonLd={articleJsonLd}
      />
      <Header />
      <main className="page_content">

        {/* Page Header */}
        <section
          className="page_header text-center bg-dark section_decoration overflow-hidden py-5"
          style={{ backgroundImage: "url('assets/images/backgrounds/page_header_bg_1.webp')" }}
        >
          <div className="container">
            <span
              className="badge bg-warning text-dark px-3 py-2 text-uppercase mb-3 font-weight-bold"
              style={{ letterSpacing: '1px' }}
            >
              <i className="fa-solid fa-newspaper me-2"></i>{categoryParam}
            </span>
            <h1 className="page_title text-white mb-3">{titleParam}</h1>
            {dateParam && (
              <p className="text-white-50 small mb-0">
                <i className="fa-regular fa-calendar me-2"></i>{dateParam}
              </p>
            )}
            <ul className="breadcrumb_nav unordered_list justify-content-center mt-3">
              <li><a href="/">Home</a></li>
              <li><a href="/blog">Blog</a></li>
              <li className="active" aria-current="page">{titleParam}</li>
            </ul>
          </div>
        </section>

        {/* Article Body */}
        <section className="section_space bg-white">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-9">
                <article>
                  <p className="lead text-muted mb-4">{excerptParam}</p>
                  <div className="article_body" style={{ fontSize: '1rem', lineHeight: '1.8', color: '#334155' }}>
                    <p>{bodyParam}</p>
                  </div>
                </article>

                <div className="mt-5 p-4 bg-light rounded-4 border d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div>
                    <h2 className="h5 font-weight-bold text-dark mb-1">Have questions about {categoryParam}?</h2>
                    <p className="text-muted small mb-0">Talk to our team for a walkthrough of the relevant Mokshiq API or software.</p>
                  </div>
                  <a href="/contact_us" className="btn btn-warning font-weight-bold text-dark px-4 py-2 rounded-pill flex-shrink-0">
                    <i className="fa-solid fa-comments me-2"></i>Contact Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
      <SeoTagsFooter />
    </div>
  );
}

export default BlogDetails;